import React from 'react';

import classnames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { BringgFontIcons } from '@bringg/bringg-icons';

import { CapacityUtilization } from '../../../capacity-utilization.types';
import { PlannedDeliveryWindowsEventType } from '../../planned-delivery-windows-calendar';
import { useStores } from '../../../../../recipes';
import { Cutoff } from '../../../cutoff/cutoff';
import { ProgressBarSection } from './progress-bar-section';
import { CapacityUtilizationCard } from './capacity-utilization-card';
import { ChipsSection } from './chips-section';
import ChartPeiSection from './chart-pei-section';
import { HeaderSection } from './capacity-utilization-header-section';

import styles from './planned-delivery-windows-popover-content.module.scss';

const T_PREFIX = 'PLANNED_DELIVERY_WINDOWS_POPOVER_CONTENT.CAPACITY_UTILIZATION_CARD';

interface Props {
	title: string;
	event: PlannedDeliveryWindowsEventType;
	setPopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
	openModalWithPlannedDeliveryWindowSlot: (plannedDeliveryWindowId: number) => void;
	openRecurrenceTypeModalWithDeleteMode: (id: number) => void;
	capacityUtilization: CapacityUtilization;
	timezone: string;
}

const PlannedDeliveryWindowsPopoverContent = ({
	title,
	event,
	openModalWithPlannedDeliveryWindowSlot,
	capacityUtilization,
	timezone
}: Props) => {
	const { t } = useTranslation();
	const { servicePlansStore } = useStores();

	const fillRatioData = capacityUtilization.fillRatioData;
	const hasHandlingUnit = !!fillRatioData.handlingUnits?.max;
	const serviceAreasCount = event.service_area_ids?.length ?? 0;
	const servicePlans =
		(event.service_plan_ids?.map(id => servicePlansStore.servicePlans.get(id)?.name).filter(Boolean) as string[]) ??
		[];

	const T_RESERVED = t('PLANNED_DELIVERY_WINDOWS_POPOVER_CONTENT.PROGRESS_BAR.RESERVED');
	const reservedKg = fillRatioData.weight?.reservedKg;
	const reservedHandlingUnits = fillRatioData.handlingUnits?.reserved;
	const weightReservedLabel = reservedKg ? `+ ${reservedKg} ${T_RESERVED}` : undefined;
	const handlingUnitsReservedLabel = reservedHandlingUnits ? `+ ${reservedHandlingUnits} ${T_RESERVED}` : undefined;
	const hasReservedInGrid = !!weightReservedLabel;
	const useColumnLayout = hasReservedInGrid || !fillRatioData.weight?.maxKg;

	return (
		<div className={styles.popoverContent} onClick={e => e.stopPropagation()}>
			<HeaderSection title={title} onEditClick={() => openModalWithPlannedDeliveryWindowSlot(event.id)} />
			<div className={styles.popoverContentBody}>
				{event.cutoff !== 0 && (
					<Cutoff
						startWindowTime={event.start}
						cutoff={event.cutoff}
						timezone={timezone}
						labelClassName={styles.cutoffLabel}
					/>
				)}
				<ChipsSection servicePlans={servicePlans} serviceAreaCount={serviceAreasCount} />
				<div className={styles.divider} />
				<ChartPeiSection
					totalSlotSeconds={fillRatioData.routeDuration.totalSlotSeconds}
					reservedCapacityRatio={fillRatioData.reservedCapacity?.ratio ?? 0}
					fillRatio={capacityUtilization.fillRatio ?? 0}
					assignedCount={fillRatioData.orders.assignedCount}
					maxCount={fillRatioData.orders.maxCount ?? 0}
				/>
				<ProgressBarSection capacityUtilization={capacityUtilization} />
				<div
					className={classnames({
						[styles.cardsColumn]: useColumnLayout,
						[styles.cardsGrid]: !useColumnLayout
					})}
				>
					<CapacityUtilizationCard
						iconName={BringgFontIcons.Resources}
						footer={t(`${T_PREFIX}.RESOURCES_FOOTER`)}
						headerLabel={t(`${T_PREFIX}.RESOURCES_HEADER`)}
						usedCount={fillRatioData.resources.plannedCount}
						totalCount={fillRatioData.resources.totalCount}
					/>
					{fillRatioData.weight?.maxKg && (
						<CapacityUtilizationCard
							iconName={BringgFontIcons.Weight}
							footer={t(`${T_PREFIX}.WEIGHT_FOOTER`)}
							headerLabel={t(`${T_PREFIX}.WEIGHT_HEADER`)}
							usedCount={fillRatioData.weight.plannedKg}
							totalCount={fillRatioData.weight.maxKg}
							reservedLabel={weightReservedLabel}
						/>
					)}
				</div>
				{hasHandlingUnit && (
					<CapacityUtilizationCard
						iconName={BringgFontIcons.Pallets}
						footer={t(`${T_PREFIX}.HANDLING_UNITS_FOOTER`)}
						headerLabel={t(`${T_PREFIX}.HANDLING_UNITS_HEADER`)}
						usedCount={fillRatioData.handlingUnits.planned}
						totalCount={fillRatioData.handlingUnits.max}
						reservedLabel={handlingUnitsReservedLabel}
					/>
				)}
			</div>
		</div>
	);
};

export default observer(PlannedDeliveryWindowsPopoverContent);
